import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
} from '@dls/react-core';
import { Icon } from '@dls/react-icon';
import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Configuration,
  ConfigurationWithId,
  createConfiguration,
  getStudyAlgorithmsAxios,
  AlgorithmWithVersions,
  updateConfiguration,
} from '../../api/studykit';
import { useSnackbar } from 'notistack';
import { NotiStackAction } from '../styled/notistack-action';
import styled from 'styled-components';
import ConfigurationSideImage from '../../assets/section-left-bg.svg';
import DLSOperating from '../assets/dls-operating';
import { capitalize, Grid, MenuItem } from '@material-ui/core';
import { ControlledInput, ControlledSelect, GridFormElement } from '../form-controls';
import { StyledBlueHeading } from '../styled/styles';
import { ServerValidationError } from '../../api/axios';
import useRequest from '../../api/useRequest';
import { AsyncSubmitHandler } from '../../helpers/form-error-handling';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  studyId: string;
  configuration?: ConfigurationWithId;
}
export type ConfigurationFormFields = Configuration;

const DialogWithoutBorders = styled(Dialog)`
  .MuiDialog-paper {
    border: 0 !important;
    max-width: 960px !important;
  }
`;

const DialogTitleWithCloseButton = styled(DialogTitle)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  &&.MuiDialogTitle-root {
    padding-bottom: 1rem;
  }
`;

const DialogContentWithIconSection = styled(DialogContent)`
  &&.MuiDialogContent-root {
    padding: 0;
    margin: 0;
  }
  display: flex;
`;

const Section = styled.div`
  flex: 1 1 0px;
`;

const BlueSection = styled(Section)`
  background-image: url(${ConfigurationSideImage});
  height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FormSection = styled(Section)`
  padding: 32px;
`;

const DialogActionsWithLeftAndRightButton = styled(DialogActions)`
  &&.MuiDialogActions-root {
    justify-content: space-between;
  }
`;

const WhiteIcon = styled.div`
  width: 72px;
  height: 72px;
  color: white;
`;

export const CreateUpdateConfigurationDialog: React.FC<DialogProps> = ({
  open,
  onClose,
  studyId,
  configuration,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    watch,
    reset,
    setError,
  } = useForm<ConfigurationFormFields>({
    defaultValues: configuration,
  });

  const watchTrigger = watch('trigger.type');
  const watchAlgorithm = watch('algorithm.name');

  const { data: algorithms } = useRequest(getStudyAlgorithmsAxios(studyId));

  const onSubmit: AsyncSubmitHandler<Configuration> = async (data, event) => {
    try {
      if (configuration) {
        await updateConfiguration(studyId, configuration.id, data);
      } else {
        await createConfiguration(studyId, data);
      }
      enqueueSnackbar(`${data.name} has successfully been ${configuration ? 'edited' : 'added'}!`, {
        variant: 'success',
        action: <NotiStackAction close={closeSnackbar} />,
      });
      handleClose();
    } catch (error) {
      if (error instanceof ServerValidationError) {
        error.errors.forEach((e) => {
          setError(e.param as keyof ConfigurationFormFields, { message: e.msg });
        });
      } else {
        enqueueSnackbar(`Error creating ${data.name}!`, {
          variant: 'error',
          action: <NotiStackAction close={closeSnackbar} />,
        });
      }
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <DialogWithoutBorders open={open} onClose={handleClose} fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitleWithCloseButton disableTypography>
          <Typography>Add configuration</Typography>
          <Button dls_variant="quietDefault" onClick={handleClose}>
            <Icon name={'Cross'} size={'m'} />
          </Button>
        </DialogTitleWithCloseButton>

        <DialogContentWithIconSection dividers>
          <BlueSection>
            <WhiteIcon>
              <DLSOperating />
            </WhiteIcon>
          </BlueSection>
          <FormSection>
            <StyledBlueHeading variant="h2" style={{ fontWeight: 400 }}>
              Add configuration
            </StyledBlueHeading>
            <Grid container spacing={3}>
              <GridFormElement label="Configuration name" md={12}>
                <ControlledInput
                  control={control}
                  name="name"
                  rules={{ required: 'Configuration name is required', pattern: { value: /^\S+$/, message: 'Configuration name must not contain spaces' } }}
                  inputProps={{ fullWidth: true }}
                />
              </GridFormElement>
              <GridFormElement label="Algorithm" md={6}>
                <ControlledSelect
                  control={control}
                  name={'algorithm.name'}
                  selectProps={{
                    fullWidth: true,
                  }}
                  rules={{ required: 'Algorithm is required' }}
                >
                  {algorithms?.map((d: AlgorithmWithVersions) => (
                    <MenuItem value={d.model} key={d.model}>
                      <span>{d.model}</span>
                    </MenuItem>
                  ))}
                </ControlledSelect>
              </GridFormElement>
              <GridFormElement label="Version" md={6}>
                <ControlledSelect
                  control={control}
                  name={'algorithm.version'}
                  selectProps={{
                    fullWidth: true,
                  }}
                  rules={{ required: 'Version is required' }}
                >
                  {watchAlgorithm &&
                    algorithms
                      ?.find((a: AlgorithmWithVersions) => a.model === watchAlgorithm)
                      ?.versions.map((v) => (
                        <MenuItem value={v} key={v}>
                          <span>{v}</span>
                        </MenuItem>
                      ))}
                </ControlledSelect>
              </GridFormElement>
              <GridFormElement label="Trigger" md={6}>
                <ControlledSelect
                  control={control}
                  name={'trigger.type'}
                  selectProps={{
                    fullWidth: true,
                    displayEmpty: true,
                  }}
                  rules={{ required: 'Trigger is required' }}
                >
                  {['manual', 'interval'].map((t) => (
                    <MenuItem value={t} key={t}>
                      <span>{capitalize(t)}</span>
                    </MenuItem>
                  ))}
                </ControlledSelect>
              </GridFormElement>
              {watchTrigger === 'interval' && (
                <GridFormElement label="Cron" md={6}>
                  <ControlledInput
                    control={control}
                    name="trigger.params.cron"
                    rules={{ required: watchTrigger === 'interval' ? 'Cron is required' : false }}
                    inputProps={{ fullWidth: true, placeholder: 'Enter cron' }}
                  />
                </GridFormElement>
              )}
            </Grid>
          </FormSection>
        </DialogContentWithIconSection>
        <DialogActionsWithLeftAndRightButton>
          <Button
            disabled={isSubmitting}
            dls_variant="quietEmphasis"
            onClick={handleClose}
            style={{ marginRight: 5, width: `12rem` }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            dls_variant="primary"
            data-testid={'submit-button'}
            style={{ marginRight: 5, width: `12rem` }}
          >
            Submit
          </Button>
        </DialogActionsWithLeftAndRightButton>
      </form>
    </DialogWithoutBorders>
  );
};
